/**
 * Small field building blocks shared by the person form sections: a label
 * with its inline error (docs/COMPONENT_PATTERNS.md — errors sit beside the
 * label via `fieldState.error`, never `<FormMessage />`), a text / textarea
 * field and a phone field, and the uppercase section micro head.
 */
import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useOrgPhoneCountry } from "./useOrgPhoneCountry";

/** The document-metaphor section head (FULLSCREEN_FORM_MODALS.md). */
export function SectionHead({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn("text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3", className)}>
      {children}
    </h3>
  );
}

/** `FormLabel` + the field's inline error, on one line. Use inside a `FormItem`. */
export function LabelWithError({ label, error }: { label: string; error?: string }) {
  return (
    <div className="flex items-center gap-2">
      <FormLabel>{label}</FormLabel>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

interface TextFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  type?: "text" | "email";
  multiline?: boolean;
  disabled?: boolean;
}

export function TextField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type = "text",
  multiline,
  disabled,
}: TextFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <LabelWithError label={label} error={fieldState.error?.message} />
          <FormControl>
            {multiline ? (
              <Textarea {...field} value={field.value ?? ""} rows={3} placeholder={placeholder} disabled={disabled} />
            ) : (
              <Input
                {...field}
                value={field.value ?? ""}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(fieldState.error && "border-destructive")}
              />
            )}
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export function PhoneField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  disabled,
}: Omit<TextFieldProps<T>, "type" | "multiline">) {
  const defaultCountry = useOrgPhoneCountry();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <LabelWithError label={label} error={fieldState.error?.message} />
          <FormControl>
            <PhoneInput value={field.value ?? ""} onChange={(v) => field.onChange(v ?? "")} placeholder={placeholder} disabled={disabled} defaultCountry={defaultCountry} hasError={!!fieldState.error} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
